import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getUserAvatarColor } from "../../../utils/kpiColors";

const AddResources = ({ closeModal }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchInputChange, setSearchInputChange] = useState("");
  const [selectMember, setSelectMember] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const usersData = useSelector((state) => state.projects.searchUserResponseData);
  const projectCreateId = useSelector(
    (state) => state.projects.createProjectResponse.project?._id
  );
  const projectCreatename = useSelector(
    (state) => state.projects.createProjectResponse.project?.name
  );

  useEffect(() => {
    if (searchInputChange?.trim()) {
      dispatch.projects.searchUserData(searchInputChange);
    }
  }, [searchInputChange, dispatch]);

  const addMember = (member) => {
    const exists = selectMember.some((el) => el._id === member._id);
    if (!exists) {
      setSelectMember((prev) => [...prev, member]);
    } else {
      toast.error("This member is already added!");
    }
    setSearchInputChange("");
  };

  const removeMember = (member) => {
    setSelectMember((prev) => prev.filter((m) => m._id !== member._id));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = selectMember.map((user) => ({
      project_id: projectCreateId,
      user_id: user._id,
      status: "active",
    }));

    try {
      const response = await dispatch.projects.inviteMember(payload);
      const res = response?.data || response;
      if (res?.message) {
        if (res?.success || response?.status === 200) {
          toast.success(res.message);
          if (closeModal) closeModal(true);
          navigate("/projects");
        } else {
          toast.error(res.message);
        }
      } else {
        toast.success("Members added successfully");
        if (closeModal) closeModal(true);
        navigate("/projects");
      }
    } catch (err) {
      toast.error(err?.response?.error || err?.message || "Failed to add members");
    } finally {
      setSubmitting(false);
    }
  };

  const searchResults = usersData?.data?.result || [];

  return (
    <div className="w-full">
      {/* Header & Stepper */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Assign Team Members
            </h2>
            <p className="text-xs text-slate-500 font-medium">
              Search and add developers or managers to the project
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shadow-xs">
              2
            </span>
            <span className="text-xs font-semibold text-slate-400 hidden sm:inline">
              of 2 steps
            </span>
          </div>
        </div>

        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-3">
          <div className="bg-slate-900 h-full w-full rounded-full transition-all duration-300"></div>
        </div>
      </div>

      {/* Search Input Box */}
      <div className="mb-6 relative">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 font-mono">
          Search & Add Member
        </label>
        <div className="relative">
          <svg
            className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition-all placeholder:text-slate-400 bg-white"
            value={searchInputChange}
            onChange={(e) => setSearchInputChange(e.target.value)}
            placeholder="Type member name or email..."
          />
        </div>

        {/* Live Search Overlay Results */}
        {searchInputChange?.trim().length > 0 && (
          <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
            {searchResults.length > 0 ? (
              searchResults.map((user, idx) => {
                const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
                const initials = (user.first_name?.[0] || "") + (user.last_name?.[0] || "");
                const avatarBg = getUserAvatarColor(user.email || fullName, idx);

                return (
                  <div
                    key={user._id || idx}
                    onClick={() => addMember(user)}
                    className="p-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 cursor-pointer flex items-center justify-between gap-3 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-full ${avatarBg} text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-2xs`}
                      >
                        {initials.toUpperCase() || "TM"}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-900 truncate">
                          {fullName}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 text-[10px] font-bold bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 rounded-lg transition-all shrink-0">
                      + Add
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-xs text-slate-400 font-medium">
                No matching members found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Members List */}
      <div className="mb-6">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100 mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 font-mono">
            Selected Members ({selectMember.length})
          </h3>
        </div>

        {selectMember.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {selectMember.map((member, idx) => {
              const fullName = `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email;
              const initials = (member.first_name?.[0] || "") + (member.last_name?.[0] || "");
              const avatarBg = getUserAvatarColor(member.email || fullName, idx);
              const desig = member.designation?.name || member.role?.name || "";

              return (
                <div
                  key={member._id || idx}
                  className="p-3 bg-slate-50/80 border border-slate-200/70 rounded-xl flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-full ${avatarBg} text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-2xs`}
                    >
                      {initials.toUpperCase() || "TM"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-900 truncate">
                        {fullName}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {member.email}
                      </div>
                      {desig && (
                        <div className="text-[10px] text-slate-500 font-medium truncate mt-0.5">
                          {desig}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeMember(member)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors shrink-0"
                    title="Remove member"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-xs text-slate-400 font-medium">
            No team members added to this project yet. Use the search bar above to add members.
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
        <button
          type="button"
          onClick={() => closeModal && closeModal()}
          className="px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Skip / Close
        </button>
        <button
          disabled={submitting}
          type="button"
          onClick={handleSubmit}
          className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-2xs flex items-center gap-2"
        >
          <span>{submitting ? "Saving..." : "Finish Project Setup"}</span>
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AddResources;